import { useState, useEffect } from "react";
import { auth } from "../../firebase";
import {
  sendEmailVerification, confirmPasswordReset, applyActionCode,
} from "firebase/auth";
import { IC, Btn, Field } from "../shared";

// ── Email Verify Pending ──────────────────────────────────────────────────────
// Shown after signup (or login with unverified account).
// User must click the link Firebase sent to noreply@igonow.xyz.
export const EmailVerifyPendingScreen = ({ email, onResend, onContinue, onBack }) => {
  const [resent,    setResent]    = useState(false);
  const [resending, setResending] = useState(false);
  const [checking,  setChecking]  = useState(false);
  const [err,       setErr]       = useState("");

  const handleResend = async () => {
    setResending(true); setErr("");
    try {
      await onResend();
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (e) {
      const msg = e.code === "auth/too-many-requests"
        ? "Too many attempts. Please wait a moment before resending."
        : e.message || "Couldn't resend email.";
      setErr(msg);
    }
    setResending(false);
  };

  const handleContinue = async () => {
    setChecking(true); setErr("");
    try {
      await onContinue();
    } catch (e) {
      setErr("Email not yet verified. Check your inbox and click the link.");
    }
    setChecking(false);
  };

  return (
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", background: "var(--paper)" }}>
      <div className="amodal" style={{ background: "var(--card)", borderRadius: 24, padding: "40px 28px", maxWidth: 380, width: "100%", textAlign: "center", boxShadow: "var(--shadow-lg)", border: "1.5px solid var(--border)" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(43,91,224,.10)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", color: "var(--accent)" }}>
          <div style={{ width: 30, height: 30 }}>{IC.mail}</div>
        </div>
        <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--ink)", marginBottom: 10 }}>Verify your email</h2>
        <p style={{ color: "var(--muted)", fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
          We sent a verification email to
        </p>
        <p style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 14, color: "var(--ink)", marginBottom: 20 }}>{email}</p>
        <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.6, marginBottom: 28 }}>
          Click the button in the email to verify your account, then tap <strong style={{ color: "var(--ink)" }}>Continue</strong> below.
        </p>
        {err    && <div className="apop" style={{ background: "var(--error-bg)", borderRadius: 10, padding: "10px 14px", color: "#c0392b", fontSize: 13, marginBottom: 14 }}>{err}</div>}
        {resent && <div className="apop" style={{ background: "var(--success-bg)", borderRadius: 10, padding: "10px 14px", color: "var(--success)", fontSize: 13, marginBottom: 14 }}>Verification email resent!</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Btn variant="accent" onClick={handleContinue} disabled={checking}>
            {checking ? <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "igo-spin .7s linear infinite" }}/> : "Continue →"}
          </Btn>
          <Btn variant="ghost" onClick={handleResend} disabled={resending}>{resending ? "Sending…" : "Resend Verification Email"}</Btn>
          <Btn variant="ghost" onClick={onBack}>Back to Login</Btn>
        </div>
      </div>
    </div>
  );
};

// ── Reset Password (handles ?mode=resetPassword&oobCode= from Firebase email) ─
// IMPORTANT: In Firebase Console > Authentication > Templates > Password reset,
// set the Action URL to https://igonow.xyz so Firebase sends users here.
export const ResetPasswordScreen = ({ oobCode }) => {
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [err,  setErr]  = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async () => {
    if (!form.password || !form.confirm) { setErr("Please fill both fields."); return; }
    if (form.password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (form.password !== form.confirm) { setErr("Passwords do not match."); return; }
    setBusy(true); setErr("");
    try {
      await confirmPasswordReset(auth, oobCode, form.password);
      setDone(true);
    } catch (e) {
      setErr(e.code === "auth/invalid-action-code" || e.code === "auth/expired-action-code"
        ? "This reset link has expired or already been used. Please request a new one."
        : e.message || "Something went wrong.");
    }
    setBusy(false);
  };

  return (
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", background: "var(--paper)" }}>
      <div className="amodal" style={{ background: "var(--card)", borderRadius: 24, padding: "40px 28px", maxWidth: 380, width: "100%", boxShadow: "var(--shadow-lg)", border: "1.5px solid var(--border)" }}>
        <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--ink)", marginBottom: 6 }}>Set new password</h2>
        <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 24 }}>Choose a new password for your iGo account.</p>
        {done ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: "var(--success-bg)", border: "1.5px solid var(--success-border)", borderRadius: 12, padding: "14px 16px", color: "var(--success)", fontSize: 14, fontWeight: 500 }}>
              Password updated! You can now log in.
            </div>
            <Btn variant="accent" onClick={() => window.location.href = "/"}>Go to Log In</Btn>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <Field label="New password" type="password" placeholder="••••••••" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}/>
            <Field label="Confirm new password" type="password" placeholder="••••••••" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}/>
            {err && <div style={{ background: "var(--error-bg)", border: "1px solid var(--error-border)", borderRadius: 10, padding: "10px 14px", color: "#c0392b", fontSize: 13 }}>{err}</div>}
            <Btn variant="accent" onClick={handleReset} disabled={busy}>
              {busy ? <span style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "igo-spin .7s linear infinite" }}/> : "Update Password"}
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Verify Email Action (handles ?mode=verifyEmail&oobCode= from Firebase email) ─
// Firebase redirects here after the user clicks the verification link.
// We call applyActionCode() to honour the code, then transition into the app.
export const VerifyEmailActionScreen = ({ oobCode, onVerified }) => {
  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error"
  const [err,    setErr]    = useState("");

  useEffect(() => {
    (async () => {
      try {
        await applyActionCode(auth, oobCode);
        if (auth.currentUser) await auth.currentUser.reload();
        // Remove the action params so a page reload doesn't re-run this flow.
        window.history.replaceState({}, "", window.location.pathname);
        setStatus("success");
        if (auth.currentUser?.emailVerified) onVerified?.();
      } catch (e) {
        setErr(
          e.code === "auth/invalid-action-code" || e.code === "auth/expired-action-code"
            ? "This verification link has expired or already been used."
            : "Email verification failed. Please try again or request a new link."
        );
        setStatus("error");
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const wrap  = { minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", background: "var(--paper)" };
  const card  = { background: "var(--card)", borderRadius: 24, padding: "40px 28px", maxWidth: 380, width: "100%", textAlign: "center", boxShadow: "var(--shadow-lg)", border: "1.5px solid var(--border)" };

  if (status === "loading") return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ width: 40, height: 40, border: "3px solid var(--border)", borderTopColor: "var(--accent)", borderRadius: "50%", animation: "igo-spin .7s linear infinite", margin: "0 auto 20px" }}/>
        <p style={{ color: "var(--muted)", fontSize: 15 }}>Verifying your email…</p>
      </div>
    </div>
  );

  if (status === "error") return (
    <div style={wrap}>
      <div style={card}>
        <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--ink)", marginBottom: 10 }}>Verification Failed</h2>
        <p style={{ color: "#c0392b", fontSize: 14, marginBottom: 24 }}>{err}</p>
        <Btn variant="accent" onClick={() => { window.history.replaceState({}, "", "/"); window.location.reload(); }}>Back to Sign In</Btn>
      </div>
    </div>
  );

  return (
    <div style={wrap}>
      <div style={card}>
        <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--ink)", marginBottom: 10 }}>Email Verified!</h2>
        <p style={{ color: "var(--muted)", fontSize: 14 }}>Your email has been confirmed. Taking you in…</p>
      </div>
    </div>
  );
};

export default EmailVerifyPendingScreen;
